import React, { useEffect, useRef, useState } from 'react';
import { usePopover } from '../Popover/usePopover';
import { type BpDesignSystem } from '../../types';
import { useEffectOnce } from '../../utils/useEffectOnce';
import { useDebounce } from '../../utils/useDebounce';
import { styled } from '../../styles';
import { Input } from '../Form/Input';
import { Icon } from '../Icon/Icon';
import { Box } from '../Box/Box';
import { Chip } from '../Chip/Chip';
import { Typography } from '../Typography/Typography';
import { Fade } from '../Fade/Fade';
import { Popover } from '../Popover/Popover';
import { CircularProgress } from '../Progress/CircularProgress';
import { IconButton } from '../IconButton/IconButton';
import { ButtonGroup } from '../Button/ButtonGroup';
import { Menu } from '../Menu';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { IconName } from '../Icon/types';

export namespace SelectWithFilterV2Types {
  export type Option<VALUE extends string = string> = {
    label: string;
    value: VALUE;
    icon?: IconName | React.ReactNode;
    code?: string;
  };
  export type InitOption<EXTRA_FILTER extends string = string> = Option & { filter: EXTRA_FILTER | undefined };
  export type Value<EXTRA_FILTER extends string = string> = { value: string; filter?: EXTRA_FILTER };
}

type SelectWithFilterV2Props<EXTRA_FILTER extends string> = BpDesignSystem.Props<{
  label?: string;
  placeholder?: string;
  loading?: boolean;
  searchPlaceholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  value: SelectWithFilterV2Types.Value<EXTRA_FILTER>[] | null;
  options: SelectWithFilterV2Types.Option[];
  noResultsText?: string;
  onChange(value: SelectWithFilterV2Types.Value<EXTRA_FILTER>[]): void;
  onSearch(term: string): void;
  onInit?(
    value: SelectWithFilterV2Types.Value<EXTRA_FILTER>[]
  ): Promise<SelectWithFilterV2Types.InitOption<EXTRA_FILTER>[]>;
  filter?: {
    options: SelectWithFilterV2Types.Option<EXTRA_FILTER>[];
    value: EXTRA_FILTER;
    onChange(value: EXTRA_FILTER): void;
  };
}>;

function noop() {}

export const SelectWithFilterV2 = <EXTRA_FILTER extends string = string>({
  label,
  fullWidth,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  onInit,
  onSearch,
  options,
  loading,
  filter,
  noResultsText,
  disabled,
  ...rest
}: SelectWithFilterV2Props<EXTRA_FILTER>): React.ReactNode => {
  const popover = usePopover();
  const { intl } = useIntlBds();
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounce(onSearch ?? noop);

  const [storedOptions, setStoredOptions] = useState<SelectWithFilterV2Types.InitOption<EXTRA_FILTER>[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const valueHash = value?.map((v) => v.value + v.filter).join('|') ?? '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: react on external value change only
  useEffect(() => {
    if (!valueHash && storedOptions.length) {
      setStoredOptions([]);
    }
  }, [valueHash]);

  const handleClickOnInput = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      popover.toggle(event);

      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  useEffectOnce(() => {
    if (value?.length && onInit) {
      setInitialLoading(true);
      onInit(value)
        .then((options) => {
          setStoredOptions(options);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
    onSearch?.('');
  });

  const handleUnselectAll = () => {
    setStoredOptions([]);
    onChange([]);
  };

  const handleUnselect = (optionValue: SelectWithFilterV2Types.Value<EXTRA_FILTER>) => {
    const updatedValues = value ? value.filter((val) => val.value !== optionValue.value) : [];
    setStoredOptions((prevOptions) => prevOptions.filter((opt) => opt.value !== optionValue.value));
    onChange(updatedValues);
  };

  const handleToggle = (optionValue: SelectWithFilterV2Types.Value<EXTRA_FILTER>) => {
    const selected = value?.find((val) => val.value === optionValue.value);
    if (selected) {
      handleUnselect(optionValue);
    } else {
      const updatedValues: SelectWithFilterV2Types.Value<EXTRA_FILTER>[] = value
        ? [...value, optionValue]
        : [optionValue];
      const selectedOption = options.find((opt) => opt.value === optionValue.value);
      if (selectedOption) {
        setStoredOptions((prevOptions) => [...prevOptions, { ...selectedOption, filter: optionValue.filter }]);
      }
      onChange(updatedValues);
    }
  };

  const isLoading = loading || initialLoading;

  const visibleOptions = options.filter((option) => {
    return !storedOptions.some((storedOption) => storedOption.value === option.value);
  });

  return (
    <>
      <StyledInput
        label={label}
        placeholder={!value?.length ? placeholder : undefined}
        fullWidth={fullWidth}
        disabled={disabled}
        slotProps={{
          input: {
            disabled: disabled
          },
          htmlInput: {
            readOnly: true
          }
        }}
        {...rest}
        onClick={handleClickOnInput}
        endAdornment={
          <>
            {initialLoading ? (
              <span>
                <CircularProgress shape="bpe" size={24} />
              </span>
            ) : (
              <IconButton
                sx={{ visibility: !!storedOptions?.length && !disabled ? 'visible' : 'hidden' }}
                iconName="clear"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnselectAll();
                }}
              />
            )}

            <Icon
              name="arrow_drop_down"
              sx={{ transition: 'transform 0.2s ease-in-out', transform: `rotate(${popover.open ? 180 : 0}deg)` }}
            />
          </>
        }
        startAdornment={
          storedOptions.length ? (
            <Box paddingBlock={2} sx={{ cursor: 'pointer' }} classes="chips-container">
              {storedOptions.map((storedOption) => (
                <Fade in key={storedOption.value}>
                  <span>
                    <Chip
                      size="small"
                      variant="outlined"
                      dataTest="uds-chip"
                      sx={{ margin: 1, cursor: 'pointer' }}
                      label={
                        <>
                          <Typography variant="label" large inline>
                            {storedOption.label}
                          </Typography>
                          {!!storedOption.code && <Typography inline>{storedOption.code}</Typography>}
                        </>
                      }
                      onDelete={
                        !disabled
                          ? () => {
                              handleUnselect(storedOption);
                            }
                          : undefined
                      }
                    />
                  </span>
                </Fade>
              ))}
            </Box>
          ) : (
            <span />
          )
        }
      />
      <Popover open={popover.open} anchorEl={popover.anchorEl} onClose={popover.close}>
        <Box width={400}>
          <Box margin={5}>
            <Input
              placeholder={searchPlaceholder ?? intl.DataTable.SearchInput.placeholder}
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                const term = e.target.value;
                setSearchTerm(term);
                debouncedSearch(term);
              }}
              slotProps={{
                htmlInput: {
                  ref: searchInputRef
                }
              }}
              startAdornment={<Icon name="search" />}
              endAdornment={
                loading || searchTerm ? (
                  <>
                    {loading && (
                      <span>
                        <CircularProgress shape="bpe" size={24} />
                      </span>
                    )}
                    {!!searchTerm && (
                      <IconButton
                        iconName="clear"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          debouncedSearch('');
                        }}
                      />
                    )}
                  </>
                ) : undefined
              }
            />
          </Box>
          {!!filter && (
            <Box margin={5}>
              <ButtonGroup
                orientation="horizontal"
                variant="outlined"
                color="default"
                fullWidth
                items={filter.options.map((filterOption) => {
                  return {
                    startIcon: filterOption.icon,
                    id: filterOption.value,
                    active: filterOption.value === filter.value,
                    children: filterOption.label,
                    onClick: () => filter.onChange(filterOption.value as EXTRA_FILTER)
                  };
                })}
              />
            </Box>
          )}

          <Box>
            <Menu.List sx={{ maxHeight: 300 }}>
              {visibleOptions.map((option) => {
                return (
                  <Menu.Item
                    key={option.value}
                    onClick={() =>
                      handleToggle({
                        value: option.value,
                        filter: filter?.value
                      })
                    }
                  >
                    {!!option.icon && (
                      <Menu.ItemIcon>
                        {typeof option.icon === 'string' ? <Icon name={option.icon as IconName} /> : option.icon}
                      </Menu.ItemIcon>
                    )}
                    <Menu.ItemText>
                      <Fade in>
                        <StyledItemText>
                          <span>{option.label}</span>
                          {!!option.code && (
                            <Typography small color="colorOnSurfaceVariant" display="inline">
                              {option.code}
                            </Typography>
                          )}
                        </StyledItemText>
                      </Fade>
                    </Menu.ItemText>
                  </Menu.Item>
                );
              })}
              {!visibleOptions.length && !isLoading && (
                <Menu.Item disabled>
                  <Menu.ItemText>
                    <Fade in>
                      <StyledItemText>
                        <Typography display="inline">{noResultsText || intl.CheckboxTree.noResultsLabel}</Typography>
                      </StyledItemText>
                    </Fade>
                  </Menu.ItemText>
                </Menu.Item>
              )}
            </Menu.List>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const StyledInput = styled(Input)`
    min-width: 200px;
    .MuiInputBase-root {
      min-height: var(--select-with-filter-input-min-height-md, 40px);
    }
  
    container: root-input/ inline-size;
    @container root-input (width > 1px) {
        .MuiChip-label  {
            max-width: 50cqw;
        }
    }

    @container root-input (width > 250px) {
        .MuiChip-label  {
            max-width: 60cqw;
        }
    }
    
    @container root-input (width > 350px) {
        .MuiChip-label  {
            max-width: 77cqw;
        }
    }
    .MuiInputBase-root {
        display: grid;
        grid-template-columns: auto 1fr 34px 24px;
        cursor: pointer;
        height: auto;

        input {
            cursor: pointer;
        }
    }

    .MuiChip-label {
        display: inline-flex;
        align-items: center;

        & > div {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .MuiTypography-root + .MuiTypography-root {
            margin-left: ${({ theme }) => theme.spacingSize.s};
        }
    }
`;

const StyledItemText = styled('div')`
  display: inline-flex;
  align-items: center;
  width: 100%;

  & > span:first-of-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .BpTypography-variant-body {
    margin-left: ${({ theme }) => theme.spacingSize.s};
  }
`;
